/**
 * Authentication Helper Functions
 *
 * Helper functions for handling authentication tokens, including
 * extracting and storing the upload token from signin/profile responses.
 */

/**
 * Process signin response and save tokens
 *
 * Call this after successful signin to extract and save both user token
 * and upload token from the response.
 *
 * Mobile app equivalent: loginScreen.dart:283-291
 *
 * @param {Object} response - Signin API response
 * @param {string} response.token - User token
 * @param {Object} response.api_response - Additional API data
 * @param {string} response.api_response.access - Upload service JWT token
 * @param {string} response.api_response.uuid - User UUID for S3 paths
 */
export function processSigninResponse(response) {
  // console.log('[AuthHelpers] Processing signin response');

  // Save user token
  if (response.token) {
    localStorage.setItem('token', response.token);
    // console.log('[AuthHelpers] User token saved');
  }

  // Save upload token from api_response.access
  if (response.api_response?.access) {
    localStorage.setItem('uploader_token', response.api_response.access);

    // Parse expiry from JWT token
    try {
      const payload = JSON.parse(atob(response.api_response.access.split('.')[1]));
      if (payload.exp) {
        const expiry = payload.exp * 1000; // Convert to milliseconds
        localStorage.setItem('uploader_token_expiry', expiry.toString());
        // console.log('[AuthHelpers] Upload token saved, expires:', new Date(expiry).toISOString());
      }
    } catch (error) {
      console.warn('[AuthHelpers] Could not parse upload token expiry:', error);
    }
  } else {
    console.warn('[AuthHelpers] No upload token in api_response.access');
  }

  // Save user UUID (used for S3 key generation)
  if (response.api_response?.uuid) {
    localStorage.setItem('user_uuid', response.api_response.uuid);
    // console.log('[AuthHelpers] User UUID saved:', response.api_response.uuid);
  }

  // Return summary for logging
  return {
    hasUserToken: !!response.token,
    hasUploadToken: !!response.api_response?.access,
    hasUuid: !!response.api_response?.uuid,
  };
}

/**
 * Process profile response and update tokens
 *
 * Call this after fetching user profile to refresh upload token.
 *
 * Mobile app equivalent: accountService.dart:365-373
 *
 * @param {Object} response - getMyProfile API response
 */
export function processProfileResponse(response) {
  // console.log('[AuthHelpers] Processing profile response');

  // Refresh upload token if present
  if (response.data?.api_response?.access) {
    localStorage.setItem('uploader_token', response.data.api_response.access);

    // Parse expiry
    try {
      const payload = JSON.parse(atob(response.data.api_response.access.split('.')[1]));
      if (payload.exp) {
        const expiry = payload.exp * 1000;
        localStorage.setItem('uploader_token_expiry', expiry.toString());
        // console.log('[AuthHelpers] Upload token refreshed, expires:', new Date(expiry).toISOString());
      }
    } catch (error) {
      console.warn('[AuthHelpers] Could not parse upload token expiry:', error);
    }
  }

  // Update user UUID if present
  if (response.data?.api_response?.uuid) {
    localStorage.setItem('user_uuid', response.data.api_response.uuid);
    // console.log('[AuthHelpers] User UUID updated:', response.data.api_response.uuid);
  }
}

/**
 * Clear all authentication tokens (call on logout)
 */
export function clearAuthTokens() {
  localStorage.removeItem('token');
  localStorage.removeItem('uploader_token');
  localStorage.removeItem('uploader_token_expiry');
  localStorage.removeItem('user_uuid');
  // console.log('[AuthHelpers] All auth tokens cleared');
}

/**
 * Get user UUID for S3 key generation
 * @returns {string|null} User UUID or null if not found
 */
export function getUserUuid() {
  return localStorage.getItem('user_uuid');
}

export default {
  processSigninResponse,
  processProfileResponse,
  clearAuthTokens,
  getUserUuid,
};
